/* One source's harvest schedule, on the connection's own page.
 *
 * Sits in `lq-detail-grid` beside Facts and Capabilities and above Recent activity, which is where
 * the design puts it and where it belongs: a cadence is a property of the source, and the reader
 * deciding it is the reader already looking at the source's endpoint and its last check.
 *
 * **Six states, and the four that are not "on" carry the weight.** `configured: false`, disabled,
 * enabled-not-yet-primed, suspended, needs-attention and read-only are each a different sentence
 * with a different next step, and the whole reason `presenceOf` exists is that collapsing any pair
 * of them makes a working schedule and a broken one look alike. §9's absence-is-not-zero rule, on
 * the platform's own scheduler.
 */

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { HarvestSchedule } from '@/shared/api/generated/catalog';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';

import {
  useConfigureHarvestSchedule,
  useHarvestSchedule,
  useSetHarvestScheduleEnabled,
} from '../api/scheduling';
import {
  asOutcome,
  defaultRecurrence,
  presenceOf,
  recurrenceOf,
  recurrenceProblem,
  type RecurrenceDraft,
} from '../model/presentation';
import { RecurrenceFields } from './RecurrenceFields';
import { ScheduleFacts, StateChip } from './ScheduleParts';

export interface HarvestScheduleCardProps {
  readonly connectionId: string;
  /** Whether this deployment's scheduling runtime is durable.
   *
   * `false` renders the design's durability notice on the card: the schedule stores and fires, and
   * a restart of this replica loses the in-flight timer. Passed in rather than read here, because
   * `/readiness` is one request for the whole page and a card issuing its own would poll it per
   * connection.
   */
  readonly durable: boolean;
}

export function HarvestScheduleCard({
  connectionId,
  durable,
}: HarvestScheduleCardProps): ReactElement {
  const { t } = useTranslation();
  const schedule = useHarvestSchedule(connectionId);
  const configure = useConfigureHarvestSchedule();
  const setEnabled = useSetHarvestScheduleEnabled();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<RecurrenceDraft>(defaultRecurrence());

  /* **A failed read is reported, never rendered as "not configured".** Those are a claim about this
     source and a failure of the request, and §9 records the same conflation shipping three times —
     on the anomaly screen, the evals screen and the profiler, each time as a sentence about a
     customer's data used to report a broken fetch. A 403 is its own branch below. */
  if (schedule.isError) {
    const forbidden = schedule.error instanceof ApiError && schedule.error.status === 403;
    return (
      <Card title={t('scheduling.harvest.title')}>
        <p className="lq-sched__detail">
          {forbidden
            ? t('scheduling.harvest.forbidden')
            : t('scheduling.harvest.readFailed')}
        </p>
      </Card>
    );
  }

  if (!schedule.isSuccess) {
    return (
      <Card title={t('scheduling.harvest.title')}>
        <p className="lq-sched__detail">{t('scheduling.loading')}</p>
      </Card>
    );
  }

  const stored: HarvestSchedule = schedule.data;
  const presence = presenceOf(stored);
  const outcome = asOutcome(stored.last_outcome);

  const openEditor = (): void => {
    setDraft(recurrenceOf(stored));
    configure.reset();
    setEditing(true);
  };

  const problem = recurrenceProblem(draft);

  /* The refusal the server sent, verbatim. **Not a rewritten sentence**: a 422 names the timezone
     or the field it refused and a 409 says the connection is out of service, and both are more
     specific than anything this component could compose from a status code. */
  const refusal =
    configure.error instanceof ApiError
      ? configure.error.message
      : configure.isError
        ? t('scheduling.form.saveFailed')
        : null;

  if (editing) {
    return (
      <Card title={t('scheduling.harvest.title')}>
        <RecurrenceFields
          value={draft}
          onChange={(next) => {
            setDraft(next);
          }}
        />
        {problem !== null ? (
          <p className="lq-sched__inline lq-sched--danger">{t(problem)}</p>
        ) : null}
        {refusal !== null ? (
          <p className="lq-sched__inline lq-sched--danger">{refusal}</p>
        ) : null}
        <div className="lq-sched__actions">
          <Button
            tone="primary"
            disabled={problem !== null}
            busy={configure.isPending}
            onClick={() => {
              configure.mutate(
                {
                  connectionId,
                  body: {
                    /* **Whatever it was, and off for a schedule that does not exist yet.** Editing
                       a cadence is not a decision to start harvesting, so saving must not enable a
                       schedule that was disabled — and a brand-new one is stored off, which is the
                       platform's default and the one this UI must not quietly change. */
                    enabled: stored.configured ? stored.enabled : false,
                    cadence: draft.cadence,
                    local_time: draft.localTime,
                    timezone: draft.timezone,
                    weekdays: [...draft.weekdays],
                  },
                },
                {
                  onSuccess: () => {
                    setEditing(false);
                  },
                },
              );
            }}
          >
            {t('scheduling.form.save')}
          </Button>
          <Button
            tone="secondary"
            onClick={() => {
              setEditing(false);
              configure.reset();
            }}
          >
            {t('scheduling.form.cancel')}
          </Button>
        </div>
      </Card>
    );
  }

  if (presence.kind === 'unconfigured') {
    return (
      <Card title={t('scheduling.harvest.title')}>
        <p className="lq-sched__describes">
          {t('scheduling.harvest.unconfigured.title')}
        </p>
        {/* **The design's two lines, and the server's `detail` is deliberately not substituted
            here.** That field carries one sentence covering both — it exists so a client *can*
            render the empty state without knowing which fields a missing schedule omits, which is
            a fallback rather than a mandate. Using it printed the same sentence twice, once as the
            title and once as the body, which the screen test caught. */}
        <p className="lq-sched__detail">{t('scheduling.harvest.unconfigured.body')}</p>
        <div className="lq-sched__actions">
          <Button tone="secondary" onClick={openEditor}>
            {t('scheduling.harvest.setSchedule')}
          </Button>
        </div>
      </Card>
    );
  }

  const toggling = setEnabled.isPending;

  /* **The design's read-only card, reached by a refusal rather than by a permission list.** A 403
     here refuses the *write*: `get` authorizes on `read` and `configure` on `configure`, so a
     reader holding the first and not the second sees the facts and cannot change them. There is no
     grant list on the wire for a client to check in advance, so the sentence appears once a write
     comes back refused — and the controls go with it, because a button known to be refused is one
     that invites a second press. */
  const refusedWrite =
    (configure.error instanceof ApiError && configure.error.status === 403) ||
    (setEnabled.error instanceof ApiError && setEnabled.error.status === 403);

  return (
    <Card title={t('scheduling.harvest.title')} hint={<StateChip presence={presence} />}>
      {/* Dimmed for a disabled schedule, which is the design's reduced emphasis — on the content
          and never on the header, so the chip that *says* "Disabled" stays readable. */}
      <div className={presence.kind === 'disabled' ? 'lq-sched__dimmed' : undefined}>
        <p className="lq-sched__describes">{stored.describes ?? ''}</p>
        <ScheduleFacts
          localTime={stored.local_time}
          timezone={stored.timezone}
          cadence={stored.cadence}
          weekdays={stored.weekdays}
          nextRunAt={stored.next_run_at ?? null}
          lastRunAt={stored.last_run_at ?? null}
          outcome={outcome}
          enabled={stored.enabled}
        />
        {stored.last_detail !== null && stored.last_detail !== undefined ? (
          <p className="lq-sched__detail">{stored.last_detail}</p>
        ) : null}
      </div>

      {presence.kind === 'disabled' ? (
        <p className="lq-sched__note">{t('scheduling.state.disabled.note')}</p>
      ) : null}

      {presence.kind === 'suspended' ? (
        <>
          <p className="lq-sched__inline lq-sched--warn">
            {t('scheduling.state.suspended.reason', { reason: presence.reason })}
          </p>
          <p className="lq-sched__note">{t('scheduling.state.suspended.note')}</p>
        </>
      ) : null}

      {/* **`needs_attention` comes from the server, and only `refused` and `failed` set it.** A
          deferral and a skip deliberately stay quiet: a screen that highlighted every deferral
          would train a reader to ignore the highlight, which is the one thing that makes a genuine
          refusal unreadable. Read from the payload rather than re-derived, so this and the API
          cannot disagree about which rows are urgent. */}
      {stored.needs_attention === true ? (
        <p className="lq-sched__inline lq-sched--danger">
          {t('scheduling.state.needsAttention')}
        </p>
      ) : null}

      {!durable ? (
        <p className="lq-sched__inline lq-sched--warn">
          {t('scheduling.state.notDurable')}
        </p>
      ) : null}

      {setEnabled.isError && !refusedWrite ? (
        <p className="lq-sched__inline lq-sched--danger">
          {setEnabled.error instanceof ApiError
            ? setEnabled.error.message
            : t('scheduling.form.toggleFailed')}
        </p>
      ) : null}

      {refusedWrite ? (
        <p className="lq-sched__note">{t('scheduling.harvest.forbidden')}</p>
      ) : (
        <div className="lq-sched__actions">
          <Button tone="secondary" onClick={openEditor}>
            {t('scheduling.form.edit')}
          </Button>
          {/* Its own control and its own request, matching the API's split route: a reader disabling
            a schedule must not have to restate its cadence, because restating it is how a cadence
            gets changed by accident in the same action. */}
          <Button
            tone="secondary"
            busy={toggling}
            onClick={() => {
              setEnabled.mutate({ id: connectionId, enabled: !stored.enabled });
            }}
          >
            {stored.enabled ? t('scheduling.form.disable') : t('scheduling.form.enable')}
          </Button>
        </div>
      )}
    </Card>
  );
}
